import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { getSector } from "../data/sectors";
import { services } from "../data/services";
import { useCases } from "../lib/cases";
import { useSEO, useJsonLd, breadcrumbLd } from "../lib/seo";
import { useLang } from "../i18n";
import { PageHero, SectionHead } from "../components/Sections";
import { CTABanner } from "../components/Footer";
import { CaseCard } from "../components/CaseCard";
import { FadeUp } from "../components/motion";

const Pillars = ({ pillars, t }) => (
  <div className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2" data-testid="sector-pillars">
    {pillars.map((p, i) => (
      <FadeUp key={p.t} delay={i * 0.06}>
        <div className="h-full bg-ink p-8 sm:p-10">
          <span className="font-mono text-xs text-gold">0{i + 1}</span>
          <h3 className="mt-4 font-display text-xl font-semibold text-white">{t(p.t)}</h3>
          <p className="mt-3 text-sm leading-relaxed text-mist sm:text-base">{t(p.d)}</p>
        </div>
      </FadeUp>
    ))}
  </div>
);

const RelatedServices = ({ slugs, t, href }) => (
  <ul className="mt-8 divide-y divide-white/10 border-y border-white/10" data-testid="sector-services">
    {slugs.map((slug) => {
      const s = services.find((x) => x.slug === slug);
      return (
        <li key={slug}>
          <Link
            to={href(`/services/${s.slug}`)}
            className="group flex items-center justify-between gap-6 py-5 transition-colors hover:text-gold"
          >
            <span>
              <span className="font-display text-lg font-semibold text-white group-hover:text-gold sm:text-xl">{t(s.name)}</span>
              <span className="mt-1 block text-sm text-mist">{t(s.short)}</span>
            </span>
            <ArrowUpRight className="h-5 w-5 shrink-0 text-white/30 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
          </Link>
        </li>
      );
    })}
  </ul>
);

export default function Sector() {
  const { slug } = useParams();
  const { t, href } = useLang();
  const sector = getSector(slug);
  const cases = useCases();
  useSEO(sector ? t(sector.seoTitle) : "Sectors — YUXEL", sector ? t(sector.seoDesc) : "YUXEL sectors", {
    image: sector?.cover?.startsWith("/") ? sector.cover : undefined,
  });
  useJsonLd(
    "breadcrumb",
    sector && breadcrumbLd([{ name: "YUXEL", path: href("/") }, { name: t(sector.name), path: href(`/sectors/${sector.slug}`) }])
  );

  if (!sector) {
    return (
      <div className="mx-auto max-w-3xl px-5 pb-32 pt-44 text-center">
        <h1 className="page-display-title text-white">{t("Sector not found")}</h1>
        <Link to={href("/")} className="mt-8 inline-block text-gold underline-offset-4 hover:underline">
          {t("Back to home")}
        </Link>
      </div>
    );
  }

  const related = sector.caseSlugs.map((s) => cases.find((c) => c.slug === s)).filter(Boolean);

  return (
    <>
      <PageHero eyebrow={t(sector.name)} title={t(sector.h1)} sub={t(sector.seoDesc)} img={sector.cover} alt={t(sector.h1)} />

      <section className="mx-auto max-w-7xl px-5 pb-20 sm:px-8" aria-label="Overview">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <FadeUp className="lg:col-span-7">
            <p className="text-lg leading-relaxed text-white/85 sm:text-xl" data-testid="sector-intro">
              {t(sector.intro)}
            </p>
          </FadeUp>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-3 gap-px bg-white/10" data-testid="sector-stats">
              {sector.stats.map((s) => (
                <div key={s.l} className="bg-panel p-5">
                  <p className="font-display text-xl font-semibold text-gold sm:text-2xl">{t(s.v)}</p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/60">{t(s.l)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28" aria-label="What we build">
        <SectionHead label={t("What we build")} title={t("One commercial system, four moving parts.")} />
        <div className="mt-14">
          <Pillars pillars={sector.pillars} t={t} />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8" aria-label="Related services">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead label={t("Services")} title={t("The disciplines behind it.")} />
          </div>
          <div className="lg:col-span-8">
            <RelatedServices slugs={sector.serviceSlugs} t={t} href={href} />
          </div>
        </div>
      </section>

      {related.length > 0 && <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28" aria-label="Case studies">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead label={t("Selected projects")} title={t("Proof from the sector.")} />
          <Link to={href("/work")} data-testid="sector-all-work" className="group inline-flex items-center gap-2 text-sm font-semibold text-gold">
            {t("All projects")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="sector-cases">
          {related.map((c) => (
            <CaseCard key={c.slug} c={c} />
          ))}
        </div>
      </section>}

      <CTABanner title={t(sector.ctaTitle)} sub={t(sector.ctaSub)} testid="sector-cta" />
    </>
  );
}
